import { supabase } from '@/integrations/supabase/client';

export type TechStackStatus = 'considering' | 'owned' | 'tried';
export type AddedFrom = 'chat' | 'browse' | 'comparison' | 'detail';

export interface TechStackItem {
  id: string;
  user_id: string;
  product_id: string;
  status: TechStackStatus;
  priority: number;
  notes: string | null;
  added_from: AddedFrom | null;
  created_at: string;
  updated_at: string;
  product?: {
    id: string;
    slug: string;
    title: string;
    subtitle: string | null;
    image: string | null;
    price: number | null;
    price_duration: string | null;
    categories: any;
    rating: number | null;
  };
}

/**
 * Get user's tech stack items with product details
 */
export async function getTechStack(userId: string): Promise<TechStackItem[]> {
  const { data, error } = await supabase
    .from('user_tech_stack')
    .select(`
      *,
      product:products(
        id,
        slug,
        title,
        subtitle,
        image,
        price,
        price_duration,
        categories,
        rating
      )
    `)
    .eq('user_id', userId)
    .order('priority', { ascending: false })
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data || []) as TechStackItem[];
}

/**
 * Add product to tech stack
 */
export async function addToTechStack(params: {
  userId: string;
  productId: string;
  status?: TechStackStatus;
  addedFrom?: AddedFrom;
  notes?: string;
}) {
  const { data, error } = await supabase
    .from('user_tech_stack')
    .insert({
      user_id: params.userId,
      product_id: params.productId,
      status: params.status || 'considering',
      added_from: params.addedFrom || 'browse',
      notes: params.notes || null,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Update tech stack item
 */
export async function updateTechStackItem(
  itemId: string,
  updates: {
    status?: TechStackStatus;
    priority?: number;
    notes?: string;
  }
) {
  const { data, error } = await supabase
    .from('user_tech_stack')
    .update(updates)
    .eq('id', itemId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Remove item from tech stack
 */
export async function removeFromTechStack(itemId: string) {
  const { error } = await supabase
    .from('user_tech_stack')
    .delete()
    .eq('id', itemId);

  if (error) throw error;
}

/**
 * Check if product is in user's tech stack
 */
export async function isInTechStack(userId: string, productId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('user_tech_stack')
    .select('id')
    .eq('user_id', userId)
    .eq('product_id', productId)
    .maybeSingle();

  if (error) throw error;
  return !!data;
}

/**
 * Get tech stack summary for AI context
 */
export async function getTechStackSummary(userId: string) {
  const items = await getTechStack(userId);
  
  const byStatus = {
    considering: items.filter(i => i.status === 'considering'),
    owned: items.filter(i => i.status === 'owned'),
    tried: items.filter(i => i.status === 'tried'),
  };

  const totalMonthlyCost = byStatus.owned
    .filter(i => i.product?.price && i.product?.price_duration === 'mo')
    .reduce((sum, i) => sum + (i.product?.price || 0), 0);

  return {
    total: items.length,
    considering: byStatus.considering.length,
    owned: byStatus.owned.length,
    tried: byStatus.tried.length,
    totalMonthlyCost,
    items: byStatus,
  };
}

/**
 * Add multiple products to tech stack (bulk insert for onboarding)
 */
export async function addManyToTechStack(
  userId: string,
  products: Array<{ productId: string; status?: TechStackStatus }>
): Promise<void> {
  const inserts = products.map((p) => ({
    user_id: userId,
    product_id: p.productId,
    status: p.status || 'considering',
    added_from: 'chat' as AddedFrom,
  }));

  const { error } = await supabase.from('user_tech_stack').insert(inserts);

  if (error) throw error;
}
